import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Card,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Paragraph,
	Spinner,
	UnderlineLink,
} from '@treams/treams-ui';

export default function Google() {
	const history = useHistory();
	const [step, setStep] = useState(0);
	const [createdProviderId, setCreatedProviderId] = useState<string | null>(null);
	const upload = useRef<HTMLInputElement>(null);
	const [create, { loading: createLoading, error: createError }] =
		graphql.useCreateIdentityProviderMutation();

	const loading = createLoading;

	const query = graphql.useServiceProvidersQuery();

	async function onMetadataFile() {
		setCreatedProviderId(null);
		const files = Array.from(upload.current?.files ?? []);

		if (!files[0]) {
			return;
		}

		try {
			const createResult = await create({
				variables: { input: { metadataFile: await files[0].text() } },
			});
			const newProviderId = createResult.data?.createIdentityProvider?.provider?.id;
			if (newProviderId) {
				setCreatedProviderId(newProviderId);
			}
			await query.refetch();

			upload.current!.value = '';

			setStep(step + 1);
		} catch {
			// create function returns both errors and throws things
		}
	}

	if (!query.data) {
		return <Spinner />;
	}

	return (
		<>
			<Box mb={3}>
				<Heading3>2. Open Google Admin.</Heading3>
				<Paragraph>
					<ol>
						<li>
							Ga naar{' '}
							<UnderlineLink href="https://admin.google.com/" target="_blank" rel="noreferrer">
								https://admin.google.com/
							</UnderlineLink>
							.
						</li>
						<li>
							Ga naar <strong>Apps</strong> in de sidebar van Google Admin en klik op{' '}
							<strong>Web- en mobiele apps</strong> in Google Admin.
							<Card my={2}>
								<img
									src="/images/sso/google/setup-1.png"
									width={251}
									alt="Screenshot Google Admin #1"
								/>
							</Card>
						</li>
						<li>
							Klik op <strong>App toevoegen</strong> in Google Admin en daarna op{' '}
							<strong>Aangepaste SAML-app toevoegen</strong> in het uitklapmenu in Google Admin.
							<Card my={2}>
								<img
									src="/images/sso/google/setup-2.png"
									width={337}
									alt="Screenshot Google Admin #2"
								/>
							</Card>
						</li>
						<li>
							Vul in <strong>App-naam</strong> de naam <strong>Treams</strong> in. Laat de{' '}
							<strong>Beschrijving</strong> en <strong>App-icoon</strong> leeg en klik{' '}
							<strong>Doorgaan</strong> onderaan de pagina in Google Admin.
							<Card my={2}>
								<img
									src="/images/sso/google/setup-3.png"
									width="100%"
									style={{ maxWidth: 969 }}
									alt="Screenshot Google Admin #3"
								/>
							</Card>
						</li>
					</ol>
				</Paragraph>

				{step === 0 && (
					<Flex mt={2}>
						<Button title="Volgende stap" onClick={() => setStep((step) => step + 1)} />
					</Flex>
				)}
			</Box>
			{step >= 1 && (
				<Box mb={3}>
					<Heading3>3. Geef jullie metadata XML aan ons</Heading3>
					<Paragraph>
						Klik op de knop onder <strong>Optie 1: IdP-metadata downloaden</strong>. Upload dat
						bestand vervolgens in het veld hieronder en klik op <strong>Volgende</strong> in Treams.
						Klik daarna ook op <strong>Doorgaan</strong> in Google Admin.
					</Paragraph>
					<input
						ref={upload}
						type="file"
						accept="text/xml"
						style={{ display: 'none' }}
						onChange={onMetadataFile}
					/>
					<Button
						mt={2}
						title="Metadata uploaden"
						onClick={() => {
							upload.current?.click();
						}}
						loading={loading || query.loading}
						disableSuccess={!!createError}
					/>
					{createError && <GraphQLErrorMessage error={createError.graphQLErrors[0]} />}
				</Box>
			)}
			{step >= 2 && (
				<Box mb={3}>
					<Heading3>4. Upload onze metadata XML naar Google Admin</Heading3>
					<Paragraph>
						In Google Admin wordt nu gevraagd om twee velden:
						<Box ml={3}>
							<ol>
								<li>
									ACS-URL:
									<Flex alignItems="center" mt={2}>
										<Box flexGrow={1} flexShrink={1} mr={2}>
											<Input
												value={query.data?.serviceProvider.acsURL}
												onChange={() => {}}
												disabled
											/>
										</Box>
										<Button
											title="Kopiëren"
											type="secondary"
											onClick={async () => {
												await navigator.clipboard.writeText(query.data!.serviceProvider.acsURL);
											}}
										/>
									</Flex>
								</li>
								<li>
									Entiteits-ID:
									<Flex alignItems="center" mt={2}>
										<Box flexGrow={1} flexShrink={1} mr={2}>
											<Input
												value={query.data?.serviceProvider.entityID}
												onChange={() => {}}
												disabled
											/>
										</Box>
										<Button
											title="Kopiëren"
											type="secondary"
											onClick={async () => {
												await navigator.clipboard.writeText(query.data!.serviceProvider.entityID);
											}}
										/>
									</Flex>
								</li>
							</ol>
						</Box>
						Laat de andere velden leeg of op de standaardwaarde en klik op <strong>Doorgaan</strong>{' '}
						in Google Admin.
					</Paragraph>

					{step === 2 && (
						<Flex mt={2}>
							<Button title="Volgende stap" onClick={() => setStep((step) => step + 1)} />
						</Flex>
					)}
				</Box>
			)}
			{step >= 3 && (
				<Box mb={3}>
					<Heading3>5. Rond de koppeling af in Google Admin</Heading3>
					<Paragraph>
						<Box ml={3}>
							<ol>
								<li>
									Op de laatste pagina wordt gevraagd om kenmerken en/of een groepslidmaatschap toe
									te wijzen. Dit is niet nodig. Rond af door op <strong>Voltooien</strong> te
									klikken.
								</li>
							</ol>
						</Box>
					</Paragraph>

					{step === 3 ? (
						<Flex mt={2}>
							<Button title="Volgende stap" onClick={() => setStep((step) => step + 1)} />
						</Flex>
					) : (
						<Box mt={2}>
							<Callout
								color="blue"
								title="Gelukt!"
								description="Het werk zit er alweer op! Nu kan iedereen gebruik maken van automatisch inloggen met Treams."
							/>
							<Flex mt={2}>
								<Button
									title="Afronden"
									disabled={!createdProviderId}
									onClick={() => history.push(`/settings/integrations/sso/${createdProviderId}`)}
								/>
							</Flex>
						</Box>
					)}
				</Box>
			)}
		</>
	);
}
